import React, {useState} from 'react';
import {
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Collapse,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import {ExpandMore, ExpandLess, Mic} from '@mui/icons-material';

type SubmissionStatus = 'PENDING' | 'PROCESSING' | 'COMPLETED' | 'FAILED';

interface AudioSubmission {
  id: string;
  videoURI?: string | null;
  originalFilename?: string | null;
  submissionStatus: SubmissionStatus;
  rawTranscript?: string | null;
  durationSeconds?: number | null;
  errorMessage?: string | null;
  createdAt?: string | null;
}

interface AudioSubmissionCardProps {
  submission: AudioSubmission;
  onClickSnag?: (submissionId: string) => void;
}

const statusColor: Record<
  SubmissionStatus,
  'default' | 'warning' | 'info' | 'success' | 'error'
> = {
  PENDING: 'default',
  PROCESSING: 'info',
  COMPLETED: 'success',
  FAILED: 'error',
};

function formatSeconds(secs: number): string {
  const m = Math.floor(secs / 60);
  const s = Math.floor(secs % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
}

export function AudioSubmissionCard({
  submission,
  onClickSnag: _onClickSnag,
}: AudioSubmissionCardProps) {
  const [transcriptOpen, setTranscriptOpen] = useState(false);
  const defectCount = 0;
  const filename =
    submission.originalFilename ||
    submission.videoURI?.split('/').pop() ||
    'video';
  const isProcessing =
    submission.submissionStatus === 'PROCESSING' ||
    submission.submissionStatus === 'PENDING';

  return (
    <Card variant="outlined" sx={{mb: 1}}>
      <CardContent sx={{py: 1.5, '&:last-child': {pb: 1.5}}}>
        <Stack spacing={1}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              flex={1}
              minWidth={0}
            >
              <Mic fontSize="small" color="action" />
              <Typography variant="subtitle2" noWrap sx={{flex: 1}}>
                {filename}
              </Typography>
            </Stack>
            <Stack direction="row" spacing={0.5} alignItems="center">
              {isProcessing && <CircularProgress size={14} />}
              <Chip
                label={submission.submissionStatus}
                size="small"
                color={statusColor[submission.submissionStatus]}
              />
            </Stack>
          </Box>

          {submission.durationSeconds != null && (
            <Typography variant="caption" color="text.secondary">
              Duration: {formatSeconds(submission.durationSeconds)}
            </Typography>
          )}

          {submission.submissionStatus === 'FAILED' && submission.errorMessage && (
            <Typography variant="caption" color="error.main">
              {submission.errorMessage}
            </Typography>
          )}

          {defectCount > 0 && (
            <Typography variant="caption" color="primary.main">
              {defectCount} snag{defectCount !== 1 ? 's' : ''} detected
            </Typography>
          )}

          {submission.rawTranscript && (
            <>
              <Box
                display="flex"
                alignItems="center"
                onClick={() => setTranscriptOpen((v) => !v)}
                sx={{cursor: 'pointer', userSelect: 'none'}}
              >
                <Typography variant="caption" color="text.secondary" flex={1}>
                  Transcript
                </Typography>
                <IconButton size="small">
                  {transcriptOpen ? (
                    <ExpandLess fontSize="small" />
                  ) : (
                    <ExpandMore fontSize="small" />
                  )}
                </IconButton>
              </Box>
              <Collapse in={transcriptOpen}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{
                    display: 'block',
                    maxHeight: 120,
                    overflow: 'auto',
                    whiteSpace: 'pre-wrap',
                    fontSize: '0.7rem',
                    bgcolor: 'action.hover',
                    p: 1,
                    borderRadius: 1,
                  }}
                >
                  {submission.rawTranscript}
                </Typography>
              </Collapse>
            </>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
}
